// src/views/FullBookingPage/index.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchSpaceData } from "services/spacePage.services";
import {
  confirmBasket,
  applyPromo,
  fetchBasket,
} from "services/basket.services";
import {
  fetchServiceOptions,
  getBookingById,
  fetchPlaceDates,
  postBookingStepByStep,
} from "services/booking.services";
import { updateUserData } from "services/dashboard.services";
import { getCurrentUser } from "store/auth/thunks";
import NotFoundPage from "views/NotFoundPage";
import Login from "modals/Login";
import { useModal } from "context";
import { useUTM } from "context/UTMContext";
import { useConsent } from "context/ConsentContext";
import getFilteredDispositionTypes from "helpers/getSpaceDispositionTypes";
import useQueryParam from "hooks/useQueryParam";
import { 
  formatDateToString, 
  createStableLocalDate,
  getDurationOptions,
  calculateDuration,
  timeToHour,
  hourToTime,
  getPossibleDayValues,
  parseBinaryToTimeSlots,
  formatTimeDisplay
} from "../utils/dateTimeHelpers";
import DispositionTypes from "./components/DispositionTypes";
import ServicesSection from "./components/ServicesSection";
import BookingDetails from "./components/BookingDetails";
import AdditionalComments from "./components/AdditionalComments";
import BillingInformation from "./components/BillingInformation";
import BookingOverview from "./components/BookingOverview";
import TrainingIdSelector from "./components/TrainingIdSelector";
import EditDateAndCapacityModal from "./components/EditDateAndCapacityModal";
import styles from "./styles.module.scss";

const POST_LOGIN_RETURN_URL_KEY = "post_login_return_url";

// ============================================
// LOADING OVERLAY COMPONENT
// ============================================
const LoadingOverlay = ({ message, subMessage }) => {
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.97)',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >
      <div style={{
        width: '60px',
        height: '60px',
        border: '4px solid #e0e0e0',
        borderTop: '4px solid #d51b81',
        borderRadius: '50%',
        animation: 'loadingSpin 1s linear infinite',
        marginBottom: '24px'
      }}></div>
      
      <div style={{
        fontSize: '20px',
        color: '#333333',
        fontWeight: 500,
        textAlign: 'center',
        maxWidth: '400px',
        minHeight: '30px'
      }}>
        {message}
        <span style={{
          display: 'inline-block',
          animation: 'loadingDots 1.5s steps(4, end) infinite',
          width: '24px',
          textAlign: 'left'
        }}>...</span>
      </div>
      
      <div style={{
        fontSize: '14px',
        color: '#999999',
        marginTop: '8px',
        textAlign: 'center'
      }}>
        {subMessage}
      </div>
      
      <style>{`
        @keyframes loadingSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes loadingDots {
          0% { content: ''; }
          25% { content: '.'; }
          50% { content: '..'; }
          75% { content: '...'; }
        }
      `}</style>
    </div>
  );
};

const FullBookingPage = () => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation("global");
  const { addUTMsToUrl, getUtmsForApi } = useUTM();
  const { hasConsent } = useConsent();
  const { openModal, closeModal } = useModal();
  const dispatch = useDispatch();
  const token = useSelector(state => state.auth.token);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const user = useSelector(state => state.auth.currentUser);
  const userType = useSelector(state => state.auth.userType);
  const dispositionRef = useRef(null);
  
  const bookingIdFromUrl = searchParams.get('bookingId');
  const externalID = useQueryParam("externalID");
  const calendarRef = useRef(null);
  const isPerformingRef = useRef(false);
  const loginModalOpenedRef = useRef(false);
const [loginPromptVisible, setLoginPromptVisible] = useState(false);
  const isMountedRef = useRef(true);
  const fetchTimeoutRef = useRef(null);
  const lastFetchedDateRef = useRef(null);
  const availabilityRequestIdRef = useRef(0);
  const dataLoadRequestIdRef = useRef(0);
  const servicesLoadRequestIdRef = useRef(0);
  const basketFetchRequestIdRef = useRef(0);
  const currentDayRequestIdRef = useRef(0);
  const dayServicesRef = useRef([]);
  const currentDayIdRef = useRef(null);
  const isUpdatingStep1Ref = useRef(false);
  const isUpdatingServicesRef = useRef(false);
  const isEditingRef = useRef(false);
  const serviceSaveQueue = useRef([]);
  const isServiceSaving = useRef(false);
  const isServicesSavingRef = useRef(false);
  const serviceSaveFailedRef = useRef(false);
  const serviceSaveSucceededRef = useRef(false);
  const lastFailedServiceStateRef = useRef(null);
  const isSubmittingRef = useRef(false);
  
  const [isServicesSaving, setIsServicesSaving] = useState(false);
  const [isUpdatingStep1, setIsUpdatingStep1] = useState(false);
  const [isSlotOperationInProgress, setIsSlotOperationInProgress] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [bookingDays, setBookingDays] = useState([]);
  
  const setServicesSaving = useCallback((value) => {
    if (isServicesSavingRef.current !== value) {
      isServicesSavingRef.current = value;
      setIsServicesSaving(value);
    }
  }, []);
  
  const [basketRefreshKey, setBasketRefreshKey] = useState(0);
  
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [servicesData, setServicesData] = useState({});
  const [servicesLoading, setServicesLoading] = useState(false);
  const [basketId, setBasketId] = useState(null);
  const [basketData, setBasketData] = useState(null);
  const [bookingId, setBookingId] = useState(bookingIdFromUrl);
  const [promoCode, setPromoCode] = useState("");
  const [promoAppliedDiscount, setPromoAppliedDiscount] = useState(0);
  const [cguAccepted, setCguAccepted] = useState(false);
  const [cguError, setCguError] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [slotError, setSlotError] = useState(null);
  const [selectedServiceDayId, setSelectedServiceDayId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availableDurations, setAvailableDurations] = useState([]);
  const [providerId, setProviderId] = useState(null);
  const [currentDayId, setCurrentDayId] = useState(null);
  const [isUpdatingBillingInfo, setIsUpdatingBillingInfo] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [resumeReservationAfterLogin, setResumeReservationAfterLogin] = useState(false);
  const [scrollToBilling, setScrollToBilling] = useState(false);
  
  const [billingType, setBillingType] = useState("individual");
  const [billingInfo, setBillingInfo] = useState({
    individualFirstName: "", 
    individualLastName: "",
    individualAddress: "", 
    individualPostalCode: "", 
    individualCity: "",
    companyName: "", 
    companyVatNumber: "", 
    companySiret: "",
    companyAddress: "", 
    companyPostalCode: "", 
    companyCity: "",
    vat_taxable_person: 1,
    user_status: "individual",
  });
  const [billingErrors, setBillingErrors] = useState({});
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  const [trainings, setTrainings] = useState([]);
  
  const [formData, setFormData] = useState({
    capacity: 1, 
    coworking_stations: 0, 
    booking_hours: [],
    day_services: [], 
    comment: "", 
    disposition_type: null,
    training_id: null,
    externalID: externalID,
  });
  
  const isCoworking = space?.placeUsage?.key === "coworking";
  const maxCapacity = isCoworking ? space?.coworking_stations : space?.max_capacity;
  const isTrainee = user?.trainee === 1;
  const hasDiscount = basketData?.discounted_total !== undefined && basketData?.discounted_total !== basketData?.total;
  const displayDiscount = basketData?.promo_saved_total ?? 0;
  const displayTotal = basketData?.discounted_total_with_vat ?? basketData?.total_with_vat ?? 0;
  const displayOriginalTotal = basketData?.total_with_vat ?? 0;
  const promoPercentage = basketData?.promo_code_percentage ?? 0;
  
  const durationOptions = useMemo(
    () => (space ? getDurationOptions(space.minimal_duration, t) : []),
    [space?.minimal_duration, t]
  );

  const dispositionTypesList = useMemo(
    () => {
      if (!space) return [];
      const capacity = Number(formData.capacity) || 0;
      return getFilteredDispositionTypes(space, t, capacity);
    },
    [space, t, formData.capacity]
  );

  dayServicesRef.current = formData.day_services || [];
  currentDayIdRef.current = currentDayId;

  const isAnyUpdateInProgress =
    isServicesSaving ||
    isUpdatingStep1 ||
    isSlotOperationInProgress ||
    serviceSaveQueue.current.length > 0;

  // ============================================================
  // Get UTMs for API calls
  // ============================================================
  const getUtmsForConfirmation = useCallback(() => {
    const includeGclid = hasConsent('analytics');
    return getUtmsForApi(includeGclid);
  }, [getUtmsForApi, hasConsent]);

  const getUtmsForStorage = useCallback(() => {
    const includeGclid = hasConsent('analytics');
    return getUtmsForApi(includeGclid);
  }, [getUtmsForApi, hasConsent]);
  
  // ========== HELPER FUNCTIONS ==========
  
  const getMinAllowedDate = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    return minDate;
  }, []);
  
  const updateFormData = useCallback((updates) => {
    setFormData(prev => {
      const newState = { ...prev, ...updates };
      if (updates.day_services !== undefined) {
        const cleanedServices = (updates.day_services || []).filter(day => 
          day.services && day.services.length > 0
        );
        dayServicesRef.current = cleanedServices;
        updates.day_services = cleanedServices;
      }
      return newState;
    });
    if (updates.disposition_type !== undefined) {
      setFormErrors(prev => ({ ...prev, disposition_type: null }));
    }
  }, []);
  
  // ============================================================
  // storeBookingDataForLater
  // ============================================================
  const storeBookingDataForLater = useCallback(() => {
    try {
      const utmsForStorage = getUtmsForStorage();

      const bookingData = {
        formData,
        billingInfo,
        billingType,
        promoCode,
        selectedDate:
          selectedDate instanceof Date ? selectedDate.toISOString() : selectedDate,
        selectedDuration,
        startTime,
        endTime,
        capacity: formData.capacity,
        coworking_stations: formData.coworking_stations,
        comment: formData.comment,
        disposition_type: formData.disposition_type,
        training_id: formData.training_id,
        externalID: formData.externalID,
        utmParams: utmsForStorage,
        timestamp: Date.now(),
      };

      sessionStorage.setItem(
        'pending_full_booking_data',
        JSON.stringify(bookingData)
      );

      return true;
    } catch (error) {
      console.error('[FullBookingPage] Failed to store pending booking:', error);
      return false;
    }
  }, [
    formData,
    billingInfo,
    billingType,
    promoCode,
    selectedDate,
    selectedDuration,
    startTime,
    endTime,
    getUtmsForStorage,
  ]);

  const openLoginModal = useCallback(() => {
  // Store the current URL so AuthEndpoint knows where to return after
  // a Google OAuth round-trip. Email login never leaves the page, so
  // this is only consumed by the Google path.
  const currentUrl = window.location.pathname + window.location.search;
  sessionStorage.setItem(POST_LOGIN_RETURN_URL_KEY, currentUrl);

  openModal({
    content: (
      <Login
        delegateRedirectToCaller
        onSuccess={() => {
          console.log(
            "[FullBookingPage] ✅ Login success, staying on current page"
          );
          sessionStorage.removeItem(POST_LOGIN_RETURN_URL_KEY);
          // No navigation needed — isAuthenticated flips to true, token
          // changes, and the existing loadData effect refetches the
          // booking automatically.
        }}
      />
    ),
    title: t("modal.login.title"),
  });
}, [openModal, t]);

useEffect(() => {
  // Wait until the space + booking fetch has resolved.
  if (loading) return;
  // Already authenticated → no login gate needed.
  if (isAuthenticated) return;
  // Only auto-open once per mount.
  if (loginModalOpenedRef.current) return;

  loginModalOpenedRef.current = true;
  setLoginPromptVisible(true);
  openLoginModal();
}, [loading, isAuthenticated, openLoginModal]);

const handleReopenLogin = useCallback(() => {
  loginModalOpenedRef.current = true;
  openLoginModal();
}, [openLoginModal]);

  // ============================================================
  // fetchBasketData
  // ============================================================
  const fetchBasketData = useCallback(async () => {
    if (!basketId || !token) return null;

    const requestId = ++basketFetchRequestIdRef.current;

    try {
      const response = await fetchBasket(token);

      if (
        !isMountedRef.current ||
        requestId !== basketFetchRequestIdRef.current
      ) {
        return null;
      }

      if (response?.data) {
        setBasketData(response.data);
        setBasketRefreshKey(prev => prev + 1);
        return response.data;
      }

      return null;
    } catch (err) {
      if (isMountedRef.current && requestId === basketFetchRequestIdRef.current) {
        console.error('[fetchBasketData] Failed:', err);
      }
      return null;
    }
  }, [basketId, token]);

  // ============================================================
  // fetchCurrentDayId
  // ============================================================
  const fetchCurrentDayId = useCallback(async () => {
    if (!bookingId || !token) return null;

    const requestId = ++currentDayRequestIdRef.current;

    try {
      const bookingData = await getBookingById(bookingId, token, 2);

      if (
        !isMountedRef.current ||
        requestId !== currentDayRequestIdRef.current
      ) {
        return null;
      }

      const bookingDays = bookingData?.booking_days || [];
      if (bookingDays.length === 0) {
        return null;
      }

      const matchingDay =
        formData.booking_hours.length > 0
          ? bookingDays.find(day =>
              formData.booking_hours.some(slot => slot.date === day.date)
            )
          : null;

      const dayId = matchingDay?.id || bookingDays[0]?.id || null;

      if (dayId) {
        setCurrentDayId(dayId);
      }

      return dayId;
    } catch (err) {
      if (isMountedRef.current) {
        console.error('[fetchCurrentDayId] Failed:', err);
      }
      return null;
    }
  }, [bookingId, token, formData.booking_hours]);

  const updateBillingInfoOnServer = useCallback(async (updatedInfo) => {
    if (!token || !user) return false;

    setIsUpdatingBillingInfo(true);
    try {
      const userDataUpdate = {
        status: billingType === "company" ? "company" : "individual",
      };

      if (billingType === "company") {
        userDataUpdate.social_reason = updatedInfo.companyName;
        userDataUpdate.company_number = updatedInfo.companySiret;
        userDataUpdate.vat_number = updatedInfo.companyVatNumber || "";
        userDataUpdate.vat_taxable_person =
          updatedInfo.vat_taxable_person !== undefined
            ? updatedInfo.vat_taxable_person
            : (updatedInfo.companyVatNumber ? 1 : 0);
        userDataUpdate.company_address = {
          address: updatedInfo.companyAddress,
          city: updatedInfo.companyCity,
          postal_code: updatedInfo.companyPostalCode,
        };
        if (updatedInfo.individualFirstName) {
          userDataUpdate.first_name = updatedInfo.individualFirstName;
        }
        if (updatedInfo.individualLastName) {
          userDataUpdate.last_name = updatedInfo.individualLastName;
        }
      } else {
        userDataUpdate.first_name = updatedInfo.individualFirstName;
        userDataUpdate.last_name = updatedInfo.individualLastName;
        userDataUpdate.address = {
          address: updatedInfo.individualAddress,
          city: updatedInfo.individualCity,
          postal_code: updatedInfo.individualPostalCode,
        };
      }

      await updateUserData(userDataUpdate, token, i18n?.language);
      await dispatch(getCurrentUser({ language: i18n?.language }));

      openModal({
        content: (
          <p>
            {t(
              "booking.fullBookingPage.billing.updateSuccess",
              "Billing information updated successfully"
            )}
          </p>
        ),
        type: "success",
        autoClose: true,
        closeDelay: 2000,
      });
      return true;
    } catch (error) {
      console.error("Failed to update user data:", error);
      openModal({
        content: (
          <p>
            {error.message ||
              t(
                "booking.fullBookingPage.billing.updateError",
                "Failed to update billing information"
              )}
          </p>
        ),
        type: "error",
        autoClose: true,
        closeDelay: 3000,
      });
      return false;
    } finally {
      setIsUpdatingBillingInfo(false);
    }
  }, [
    token,
    user,
    billingType,
    dispatch,
    i18n?.language,
    openModal,
    t,
  ]);

  const updateBillingInfoBeforeConfirm = useCallback(async () => {
    if (!token || !user) return true;
    
    try {
      const userDataUpdate = {
        status: billingType === "company" ? "company" : "individual",
      };
      
      if (billingType === "company") {
        userDataUpdate.social_reason = billingInfo.companyName;
        userDataUpdate.company_number = billingInfo.companySiret;
        userDataUpdate.vat_number = billingInfo.companyVatNumber || "";
        userDataUpdate.vat_taxable_person = billingInfo.vat_taxable_person !== undefined ? billingInfo.vat_taxable_person : (billingInfo.companyVatNumber ? 1 : 0);
        userDataUpdate.company_address = {
          address: billingInfo.companyAddress,
          city: billingInfo.companyCity,
          postal_code: billingInfo.companyPostalCode,
        };
        if (billingInfo.individualFirstName) userDataUpdate.first_name = billingInfo.individualFirstName;
        if (billingInfo.individualLastName) userDataUpdate.last_name = billingInfo.individualLastName;
      } else {
        userDataUpdate.first_name = billingInfo.individualFirstName;
        userDataUpdate.last_name = billingInfo.individualLastName;
        userDataUpdate.address = {
          address: billingInfo.individualAddress,
          city: billingInfo.individualCity,
          postal_code: billingInfo.individualPostalCode,
        };
      }
      
      await updateUserData(userDataUpdate, token, i18n?.language);
      await dispatch(getCurrentUser({ language: i18n?.language }));
      return true;
    } catch (error) {
      console.error('Failed to update billing info before confirm:', error);
      return false;
    }
  }, [token, user, billingType, billingInfo, dispatch, i18n?.language]);
  
  // ============================================================
  // updateBookingStep1 - Only for capacity, coworking, booking_hours
  // ============================================================
  const updateBookingStep1 = useCallback(async (updateDataOverrides = {}) => {
    if (!bookingId || !token) return false;
    if (isUpdatingStep1Ref.current) return false;
    
    const bookingHoursToUse = updateDataOverrides.booking_hours !== undefined 
      ? updateDataOverrides.booking_hours 
      : formData.booking_hours;
    
    if (bookingHoursToUse.length === 0 && updateDataOverrides.booking_hours === undefined) {
      return false;
    }
    
    isUpdatingStep1Ref.current = true;
    setIsUpdatingStep1(true);
    
    try {
      const updateData = {
        place_id: space?.id,
        externalID: formData.externalID || externalID,
        capacity: isCoworking ? 0 : formData.capacity,
        coworking_stations: isCoworking ? formData.coworking_stations : 0,
        booking_hours: bookingHoursToUse.map(hour => ({
          date: hour.date,
          from: hour.from,
          to: hour.to
        })),
        ...updateDataOverrides
      };
      
      console.log('[updateBookingStep1] Sending update with booking_hours:', updateData.booking_hours);
      
      const response = await postBookingStepByStep(updateData, token, 1, bookingId);
      if (response?.id) {
        await fetchBasketData();
        return true;
      }
      return false;
    } catch (err) {
      console.error('[updateBookingStep1] Failed:', err);
      return false;
    } finally {
      isUpdatingStep1Ref.current = false;
      setIsUpdatingStep1(false);
    }
  }, [
    bookingId,
    token,
    space?.id,
    isCoworking,
    formData.capacity,
    formData.coworking_stations,
    formData.booking_hours,
    formData.externalID,
    fetchBasketData,
    externalID,
  ]);
  
  // ============================================================
  // updateServicesOnServer
  // ============================================================
  const updateServicesOnServer = useCallback(async (updatedDayServices) => {
    if (!bookingId || !token) return false;
    if (isUpdatingServicesRef.current) return false;
    if (!Array.isArray(updatedDayServices)) return false;

    isUpdatingServicesRef.current = true;

    try {
      let validDayIds = bookingDays.map(day => day.id).filter(Boolean);

      const requestedDayIds = new Set(
        updatedDayServices.map(day => day.day_id).filter(Boolean)
      );

      const hasAllDayIds = validDayIds.length > 0 &&
        [...requestedDayIds].every(dayId => validDayIds.includes(dayId));

      if (!hasAllDayIds) {
        const bookingData = await getBookingById(bookingId, token, 2);
        validDayIds = bookingData?.booking_days?.map(day => day.id).filter(Boolean) || [];

        if (bookingData?.booking_days && isMountedRef.current) {
          setBookingDays(bookingData.booking_days);
        }
      }

      const validDayServices = updatedDayServices
        .filter(day => day.services && day.services.length > 0)
        .filter(day => validDayIds.includes(day.day_id))
        .map(day => ({
          day_id: day.day_id,
          services: (day.services || [])
            .map(service => ({
              service_id: service.service_id,
              quantity: Number.parseInt(service.quantity, 10),
            }))
            .filter(service =>
              Number.isFinite(service.quantity) && service.quantity > 0
            ),
        }))
        .filter(day => day.services.length > 0);

      if (validDayServices.length === 0 && updatedDayServices.length > 0) {
        return true;
      }

      const updateData = {
        basket_item_id: bookingId,
        place_id: space?.id,
        day_services: validDayServices,
      };

      const response = await postBookingStepByStep(updateData, token, 2, bookingId);
      return Boolean(response?.id);
    } catch (err) {
      console.error('[updateServicesOnServer] Failed:', err);
      return false;
    } finally {
      isUpdatingServicesRef.current = false;
    }
  }, [bookingId, token, bookingDays, space?.id]);

  // ============================================================
  // Service Queue Processor
  // ============================================================
  const processServiceQueue = useCallback(async () => {
    if (isServiceSaving.current) {
      return;
    }
    
    if (serviceSaveQueue.current.length === 0) {
      setServicesSaving(false);
      return;
    }

    const maxRetries = 3;
    const itemsWithRetries = serviceSaveQueue.current.map(item => ({
      ...item,
      retryCount: item.retryCount || 0
    }));
    serviceSaveQueue.current = itemsWithRetries;
    
    isServiceSaving.current = true;
    setServicesSaving(true);
    
    try {
      while (serviceSaveQueue.current.length > 0) {
        const nextItem = serviceSaveQueue.current.shift();
        if (!nextItem) continue;

        const { updatedDayServices } = nextItem;
        const retryCount = nextItem.retryCount || 0;
        
        const filteredDayServices = updatedDayServices
          .filter(day => day.services && day.services.length > 0)
          .map(day => ({
            day_id: day.day_id,
            services: day.services.filter(s => s.quantity > 0)
          }))
          .filter(day => day.services.length > 0);
        
        if (filteredDayServices.length === 0) {
          continue;
        }
        
        try {
          const result = await updateServicesOnServer(filteredDayServices);
          
          if (result) {
            serviceSaveSucceededRef.current = true;
            continue;
          }

          throw new Error('Service update returned an unsuccessful response');
        } catch (error) {
          console.error(`[ServiceQueue] Save attempt ${retryCount + 1} failed:`, error);

          if (retryCount + 1 < maxRetries) {
            serviceSaveQueue.current.unshift({
              ...nextItem,
              retryCount: retryCount + 1
            });
            break;
          }

          serviceSaveFailedRef.current = true;
          lastFailedServiceStateRef.current = nextItem.updatedDayServices;
          serviceSaveQueue.current = [];
          console.error('[ServiceQueue] Max retries exceeded. Blocking confirmation.');
          break;
        }
      }
    } catch (error) {
      console.error('[ServiceQueue] Queue processing error:', error);
      serviceSaveFailedRef.current = true;
      serviceSaveQueue.current = [];
    } finally {
      isServiceSaving.current = false;
      
      if (isMountedRef.current) {
        if (serviceSaveQueue.current.length === 0) {
          setServicesSaving(false);
        } else {
          setTimeout(() => {
            if (isMountedRef.current) processServiceQueue();
          }, 50);
        }
      }
    }
  }, [updateServicesOnServer, fetchBasketData, setServicesSaving]);

  const queueServiceSave = useCallback((updatedDayServices) => {
    if (!Array.isArray(updatedDayServices)) return;

    serviceSaveFailedRef.current = false;
    lastFailedServiceStateRef.current = null;

    const lastQueued = serviceSaveQueue.current.at(-1);
    if (lastQueued &&
        JSON.stringify(lastQueued.updatedDayServices) === JSON.stringify(updatedDayServices)) {
      return;
    }

    if (isServiceSaving.current) {
      serviceSaveQueue.current = [{
        updatedDayServices,
        retryCount: 0,
      }];
    } else {
      serviceSaveQueue.current.push({
        updatedDayServices,
        retryCount: 0,
      });
    }

    setServicesSaving(true);

    if (!isServiceSaving.current) {
      void processServiceQueue();
    }
  }, [processServiceQueue, setServicesSaving]);

  const retryFailedServiceSave = useCallback(() => {
    if (
      !serviceSaveFailedRef.current ||
      !lastFailedServiceStateRef.current ||
      isServiceSaving.current
    ) {
      return false;
    }

    const failedState = lastFailedServiceStateRef.current;

    serviceSaveFailedRef.current = false;
    serviceSaveQueue.current = [{
      updatedDayServices: failedState,
      retryCount: 0,
    }];

    setServicesSaving(true);
    void processServiceQueue();
    return true;
  }, [processServiceQueue, setServicesSaving]);

  // ============================================================
  // reloadServices
  // ============================================================
  const reloadServices = useCallback(async () => {
    if (!bookingId || !token) return;

    setServicesLoading(true);

    try {
      const currentLang = i18n?.language || 'en';

      const [bookingData, services] = await Promise.all([
        getBookingById(bookingId, token, 2),
        fetchServiceOptions(bookingId, currentLang),
      ]);

      if (!isMountedRef.current) return;

      if (bookingData?.booking_days) {
        setBookingDays(bookingData.booking_days);

        const firstDayId = bookingData.booking_days[0]?.id ?? null;
        setCurrentDayId(firstDayId);
        setSelectedServiceDayId(firstDayId);
      }

      setServicesData(services || {});
    } catch (err) {
      if (isMountedRef.current) {
        console.error('[reloadServices] Failed:', err);
      }
    } finally {
      if (isMountedRef.current) {
        setServicesLoading(false);
      }
    }
  }, [bookingId, token, i18n?.language]);

  // ========== SLOT AND DATE FUNCTIONS ==========
  
  const isTimeOverlapping = useCallback((start, end, bookedSlotsList) => {
    if (!bookedSlotsList || bookedSlotsList.length === 0) return false;
    const startHour = timeToHour(start);
    const endHour = timeToHour(end);
    return bookedSlotsList.some(booked => {
      const bookedStart = timeToHour(booked.from);
      const bookedEnd = timeToHour(booked.to);
      return (startHour < bookedEnd && endHour > bookedStart);
    });
  }, []);
  
  const getDaySchedules = useCallback((date) => {
    if (!date) return [];

    const stableDate = createStableLocalDate(date);
    if (!stableDate) return [];

    const jsDayOfWeek = stableDate.getDay();
    const possibleDayValues = getPossibleDayValues(jsDayOfWeek);

    return (space?.opening_hours || []).filter(slot => {
      const slotDay = Number(slot.day);
      return possibleDayValues.includes(slotDay);
    });
  }, [space?.opening_hours]);

  const isWorkingDay = useCallback((date) => {
    if (!space?.opening_hours || space.opening_hours.length === 0) return false;
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return false;
    const jsDayOfWeek = stableDate.getDay();
    const possibleDayValues = getPossibleDayValues(jsDayOfWeek);
    return space.opening_hours.some(slot => {
      const slotDay = Number(slot.day);
      return possibleDayValues.includes(slotDay);
    });
  }, [space?.opening_hours]);
  
  const getAvailableTimeSlots = useCallback((date, duration, bookedSlotsList) => {
    if (!date || !Number.isFinite(duration) || duration <= 0) return [];
    if (!isWorkingDay(date)) return [];

    const daySchedules = getDaySchedules(date);
    if (daySchedules.length === 0) return [];

    const allSlots = [];
    const durationMinutes = duration * 60;

    for (const daySchedule of daySchedules) {
      const openingTime = daySchedule?.from?.slice(0, 5);
      const closingTime = daySchedule?.to?.slice(0, 5);
      const openingHour = timeToHour(openingTime);
      const closingHour = timeToHour(closingTime);

      if (
        !Number.isFinite(openingHour) ||
        !Number.isFinite(closingHour) ||
        closingHour <= openingHour ||
        duration > closingHour - openingHour
      ) {
        continue;
      }

      const openingMinutes = Math.round(openingHour * 60);
      const closingMinutes = Math.round(closingHour * 60);

      for (
        let currentMinutes = openingMinutes;
        currentMinutes + durationMinutes <= closingMinutes;
        currentMinutes += 30
      ) {
        const currentHour = Math.floor(currentMinutes / 60);
        const currentMinute = currentMinutes % 60;
        const slotStart =
          `${currentHour.toString().padStart(2, '0')}:` +
          `${currentMinute.toString().padStart(2, '0')}`;

        allSlots.push(slotStart);
      }
    }

    return [...new Set(allSlots)].filter(slotStart => {
      const slotEnd = hourToTime(timeToHour(slotStart) + duration);
      return !isTimeOverlapping(slotStart, slotEnd, bookedSlotsList);
    });
  }, [isWorkingDay, getDaySchedules, isTimeOverlapping]);

  const fetchBookingsForDate = useCallback(async (date) => {
    if (!date || !space?.id) return [];
    const formattedDate = formatDateToString(date);
    const cacheKey = `${space.id}:${token || 'anonymous'}:${formattedDate}`;

    if (lastFetchedDateRef.current === cacheKey) return null;

    lastFetchedDateRef.current = cacheKey;
    
    try {
      const response = await fetchPlaceDates(space.id, formattedDate, formattedDate, token);
      if (response?.success && response?.data) {
        const booked = [];
        if (response.data.reservations) {
          response.data.reservations.forEach(res => {
            if (
              res?.date === formattedDate &&
              typeof res.from === 'string' &&
              typeof res.to === 'string'
            ) {
              booked.push({
                from: res.from.slice(0, 5),
                to: res.to.slice(0, 5),
              });
            }
          });
        }
        if (response.data.private_reservations) {
          response.data.private_reservations.forEach(res => {
            if (
              res?.start_date &&
              res?.end_date &&
              typeof res.from === 'string' &&
              typeof res.to === 'string' &&
              formattedDate >= res.start_date &&
              formattedDate <= res.end_date
            ) {
              booked.push({
                from: res.from.slice(0, 5),
                to: res.to.slice(0, 5),
              });
            }
          });
        }
        return booked;
      }

      console.warn('[fetchBookingsForDate] Availability response was unsuccessful:', response);
      lastFetchedDateRef.current = null;
      return null;
    } catch (err) {
      console.error('[fetchBookingsForDate] Error:', err);
      lastFetchedDateRef.current = null;
      return null;
    }
  }, [space?.id, token]);

  // ============================================================
  // Check if any updates are in progress
  // ============================================================
  const isAnyUpdateInProgressForReservation = useCallback(() => {
    return (
      isServicesSavingRef.current ||
      isUpdatingStep1Ref.current ||
      serviceSaveQueue.current.length > 0 ||
      isSlotOperationInProgress
    );
  }, [isSlotOperationInProgress]);

  // ============================================================
  // Wait for all pending updates to complete
  // ============================================================
  const waitForAllUpdates = useCallback(async () => {
    const maxWaitTime = 30000;
    const startTime = Date.now();
    let waitCount = 0;
    
    return new Promise((resolve, reject) => {
      const checkAllDone = () => {
        const elapsed = Date.now() - startTime;
        waitCount++;

        if (serviceSaveFailedRef.current) {
          reject(new Error('A service update could not be saved. Please review the selected services and try again.'));
          return;
        }
        
        const allDone = !isServicesSavingRef.current && 
                       !isUpdatingStep1Ref.current && 
                       serviceSaveQueue.current.length === 0 &&
                       !isSlotOperationInProgress;
        
        if (allDone) {
          resolve();
          return;
        }

        if (elapsed > maxWaitTime) {
          console.warn('[FullBookingPage] Wait for updates timed out after 30s');
          console.warn('[FullBookingPage] Queue status:', {
            isServicesSaving: isServicesSavingRef.current,
            isUpdatingStep1: isUpdatingStep1Ref.current,
            queueLength: serviceSaveQueue.current.length,
            isSlotOperation: isSlotOperationInProgress,
            waitCount
          });
          reject(new Error('Saving your booking changes is taking too long. Please try again.'));
          return;
        }
        
        if (serviceSaveQueue.current.length > 0 && !isServiceSaving.current) {
          processServiceQueue();
        }
        
        setTimeout(checkAllDone, 200);
      };
      
      checkAllDone();
    });
  }, [processServiceQueue, isSlotOperationInProgress]);

  // ============================================================
  // handleDateSelect
  // ============================================================
  const handleDateSelect = useCallback((date) => {
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return;
    
    setSelectedDate(stableDate);
    setStartTime(null);
    setEndTime(null);
    setSelectedDuration(null);
    setBookedSlots([]);
    setAvailableDurations([]);
    setAvailableSlots([]);
    setSlotError(null);
    
    if (formData.day_services.length > 0) {
      updateFormData({ day_services: [] });
    }
    setCurrentDayId(null);
    setServicesData({});
    setSelectedServiceDayId(null);
    
    lastFetchedDateRef.current = null;
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
  }, [formData.day_services.length, updateFormData]);
  
  // ============================================================
  // handleAddTimeSlot
  // ============================================================
  const handleAddTimeSlot = useCallback(async () => {
    if (isSlotOperationInProgress) return;

    if (!selectedDate || !startTime || !endTime) {
      setSlotError(t("booking.fullBookingPage.errors.slotRequired"));
      return;
    }

    const formattedDate = formatDateToString(selectedDate);
    const formattedStart = formatTimeDisplay(startTime);
    const formattedEnd = formatTimeDisplay(endTime);

    if (!formattedStart || !formattedEnd ||
        formattedStart.length !== 5 || formattedEnd.length !== 5) {
      setSlotError(t("booking.compactBookingForm.invalidSlot"));
      return;
    }

    const startHour = timeToHour(formattedStart);
    const endHour = timeToHour(formattedEnd);

    if (
      !Number.isFinite(startHour) ||
      !Number.isFinite(endHour) ||
      startHour >= endHour
    ) {
      setSlotError(t("booking.compactBookingForm.invalidSlot"));
      return;
    }

    const newSlot = {
      date: formattedDate,
      from: formattedStart,
      to: formattedEnd
    };

    const existingSlots = formData.booking_hours || [];

    const isOverlapping = existingSlots.some(slot => {
      if (slot.date !== newSlot.date) return false;
      const start1 = timeToHour(slot.from);
      const end1 = timeToHour(slot.to);
      const start2 = timeToHour(newSlot.from);
      const end2 = timeToHour(newSlot.to);
      return (start1 < end2 && end1 > start2);
    });

    if (isOverlapping) {
      setSlotError(t("booking.fullBookingPage.errors.slotOverlap"));
      return;
    }

    setSlotError(null);
    setIsCalendarOpen(false);

    const updatedSlots = [...existingSlots, newSlot];

    updateFormData({
      booking_hours: updatedSlots,
      ...(formData.day_services?.length > 0 ? { day_services: [] } : {}),
    });

    setSelectedDate(null);
    setSelectedDuration(null);
    setStartTime(null);
    setEndTime(null);
    setAvailableSlots([]);
    setAvailableDurations([]);
    setBookedSlots([]);
    lastFetchedDateRef.current = null;
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    setIsSlotOperationInProgress(true);

    try {
      if (bookingId && token) {
        const updateSuccess = await updateBookingStep1({ booking_hours: updatedSlots });
        if (!updateSuccess) {
          throw new Error('Failed to save the selected time slot');
        }
        await reloadServices();
        await fetchBasketData();
      }
    } catch (err) {
      console.error('[handleAddTimeSlot] Backend error:', err);
      updateFormData({ booking_hours: existingSlots });
      setSlotError(t("booking.fullBookingPage.errors.slotNoLongerAvailable"));
    } finally {
      setIsSlotOperationInProgress(false);
    }
  }, [selectedDate, startTime, endTime, formData.booking_hours, formData.day_services, bookingId, token, t, updateFormData, updateBookingStep1, reloadServices, fetchBasketData, isSlotOperationInProgress]);

  // ============================================================
  // handleRemoveTimeSlot
  // ============================================================
  const handleRemoveTimeSlot = useCallback(async (index) => {
    if (isSlotOperationInProgress) return;

    const existingSlots = formData.booking_hours || [];
    
    if (existingSlots.length <= 1) {
      setSlotError(t("booking.fullBookingPage.errors.cannotRemoveLastSlot"));
      return;
    }
    
    if (index < 0 || index >= existingSlots.length) return;

    const updatedSlots = [...existingSlots];
    updatedSlots.splice(index, 1);
    updateFormData({ booking_hours: updatedSlots });
    
    if (formData.day_services && formData.day_services.length > 0) {
      updateFormData({ day_services: [] });
    }
    
    if (updatedSlots.length === 0) {
      setSelectedDate(null);
      setStartTime(null);
      setEndTime(null);
      setSelectedDuration(null);
      setBookedSlots([]);
      setAvailableDurations([]);
      setAvailableSlots([]);
      lastFetchedDateRef.current = null;
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    }

    setIsSlotOperationInProgress(true);

    try {
      if (bookingId && token) {
        const updateSuccess = await updateBookingStep1({ booking_hours: updatedSlots });
        if (!updateSuccess) {
          throw new Error('Failed to update the selected time slots');
        }
        await reloadServices();
        await fetchBasketData();
      }
    } catch (err) {
      console.error('[handleRemoveTimeSlot] Backend error:', err);
      updateFormData({ booking_hours: existingSlots });
      setSlotError(t("booking.fullBookingPage.errors.failedToUpdateSlots"));
    } finally {
      setIsSlotOperationInProgress(false);
    }
  }, [formData.booking_hours, formData.day_services, bookingId, token, updateFormData, updateBookingStep1, reloadServices, fetchBasketData, isSlotOperationInProgress]);
  
// ============================================================
// handleEditSave - Save changes from EditDateAndCapacityModal
// ============================================================
const handleEditSave = useCallback(async (updatedData) => {
  console.log('[EditDateAndCapacity] 🚀 STARTING EDIT SAVE');
  console.log('[EditDateAndCapacity] 📊 New capacity:', updatedData.capacity);
  console.log('[EditDateAndCapacity] 📊 Current disposition_type:', formData.disposition_type);
  
  setIsSavingEdit(true);
  setError(null);
  isEditingRef.current = true;
  
  const originalFormData = formData;
  const originalBookingDays = bookingDays;
  const originalCurrentDayId = currentDayId;
  const originalSelectedServiceDayId = selectedServiceDayId;
  const originalBookingId = bookingId;
  const originalBasketId = basketId;
  const originalServicesData = servicesData;
  let step1Persisted = false;

  try {
    const capacityChanged = updatedData.capacity !== formData.capacity || 
                           updatedData.coworking_stations !== formData.coworking_stations;
    const slotsChanged = JSON.stringify(updatedData.booking_hours) !== JSON.stringify(formData.booking_hours);
    
    const currentComment = formData.comment || "";
    const currentTrainingId = formData.training_id || null;
    const currentExternalID = formData.externalID || externalID || null;
    const currentDispositionType = formData.disposition_type;
    
    // ✅ PRESERVE disposition type by default
    let newDispositionType = currentDispositionType;

    // ✅ Only clear disposition if capacity exceeds max
    if (currentDispositionType) {
      console.log('[EditDateAndCapacity] 🔍 Checking disposition type:', currentDispositionType);
      
      // Get filtered dispositions to check max capacity
      const filteredDispositions = getFilteredDispositionTypes(space, t, updatedData.capacity);
      const layout = filteredDispositions.find(
        dt => dt.key === currentDispositionType
      );

      console.log('[EditDateAndCapacity] 📊 Found layout in filtered dispositions:', layout);

      if (layout) {
        // Use the count (max capacity) from the filtered disposition
        const maxCapacityForLayout = layout.count ?? space?.max_capacity ?? Infinity;
        const isValidForNewCapacity = updatedData.capacity <= maxCapacityForLayout;

        console.log('[EditDateAndCapacity] 📊 Disposition check:', {
          disposition: currentDispositionType,
          newCapacity: updatedData.capacity,
          maxCapacity: maxCapacityForLayout,
          isValid: isValidForNewCapacity
        });

        if (!isValidForNewCapacity) {
          console.log(`[EditDateAndCapacity] ❌ New capacity ${updatedData.capacity} exceeds max ${maxCapacityForLayout}, clearing disposition`);
          newDispositionType = null;
        } else {
          console.log(`[EditDateAndCapacity] ✅ Disposition ${currentDispositionType} is valid for capacity ${updatedData.capacity}, keeping it`);
        }
      } else {
        // If layout not found in filtered dispositions, the disposition might not be available for this capacity
        // Check if it was filtered out because capacity exceeds max
        const allDispositions = getFilteredDispositionTypes(space, t, formData.capacity);
        const originalLayout = allDispositions.find(
          dt => dt.key === currentDispositionType
        );
        
        if (originalLayout) {
          // The disposition exists but might be disabled for the new capacity
          const maxCapacityForLayout = originalLayout.count ?? space?.max_capacity ?? Infinity;
          if (updatedData.capacity > maxCapacityForLayout) {
            console.log(`[EditDateAndCapacity] ❌ New capacity ${updatedData.capacity} exceeds max ${maxCapacityForLayout}, clearing disposition`);
            newDispositionType = null;
          } else {
            // Keep it if capacity is within limits
            console.log(`[EditDateAndCapacity] ✅ Keeping disposition ${currentDispositionType}`);
          }
        } else {
          console.log(`[EditDateAndCapacity] ⚠️ Disposition ${currentDispositionType} not found, clearing`);
          newDispositionType = null;
        }
      }
    }

    // ✅ STEP 1: Save Step 1 changes
    console.log('[EditDateAndCapacity] 📤 Saving Step 1...');
    const step1Success = await updateBookingStep1({
      booking_hours: updatedData.booking_hours,
      capacity: updatedData.capacity,
      coworking_stations: updatedData.coworking_stations
    });
    
    if (!step1Success) {
      throw new Error('Failed to update booking details');
    }
    
    step1Persisted = true;
    console.log('[EditDateAndCapacity] ✅ Step 1 saved successfully');
    
    // ✅ STEP 2: CLEAR ALL SERVICES - CRITICAL to prevent old day IDs
    serviceSaveQueue.current = [];
    serviceSaveFailedRef.current = false;
    lastFailedServiceStateRef.current = null;

    updateFormData({ day_services: [] });
    dayServicesRef.current = [];
    console.log('[EditDateAndCapacity] 🗑️ Cleared all services');
    
    await waitForAllUpdates();
    
    // ✅ STEP 3: Fetch FRESH booking data with step 2
    console.log('[EditDateAndCapacity] 📤 Fetching fresh booking data...');
    const freshBookingData = await getBookingById(bookingId, token, 2);
    
    if (!freshBookingData) {
      throw new Error('Failed to fetch updated booking data');
    }
    
    console.log('[EditDateAndCapacity] 📥 Fresh booking data received, new day IDs:', 
      freshBookingData.booking_days?.map(d => d.id));
    
    // ✅ STEP 4: Update booking ID if it changed
    if (freshBookingData.id && freshBookingData.id !== bookingId) {
      setBookingId(freshBookingData.id);
    }
    
    // ✅ STEP 5: Update basket ID if it changed
    if (freshBookingData.basket_id && freshBookingData.basket_id !== basketId) {
      setBasketId(freshBookingData.basket_id);
    }
    
    // ✅ STEP 6: Update bookingDays with NEW day IDs from fresh data
    if (freshBookingData.booking_days && freshBookingData.booking_days.length > 0) {
      setBookingDays(freshBookingData.booking_days);
    }
    
    // ✅ STEP 7: Update current day ID to the NEW one
    let newCurrentDayId = null;
    if (freshBookingData.booking_days?.[0]?.id) {
      newCurrentDayId = freshBookingData.booking_days[0].id;
      setCurrentDayId(newCurrentDayId);
      setSelectedServiceDayId(newCurrentDayId);
    }
    
    // ✅ STEP 8: Update form data with fresh data
    // ✅ CRITICAL: Preserve disposition_type from local state (newDispositionType)
    const updatedFormData = {
      capacity: freshBookingData.capacity ?? updatedData.capacity,
      coworking_stations: freshBookingData.coworking_stations ?? updatedData.coworking_stations,
      booking_hours: freshBookingData.booking_hours || updatedData.booking_hours,
      day_services: [],
      // ✅ Keep disposition_type from local state (preserved or cleared based on validation)
      disposition_type: newDispositionType,
      comment: currentComment,
      training_id: currentTrainingId,
      externalID: currentExternalID || freshBookingData.externalID || null
    };
    
    console.log('[EditDateAndCapacity] 📊 Updated form data disposition_type:', updatedFormData.disposition_type);
    
    updateFormData(updatedFormData);
    dayServicesRef.current = [];
    
    // ✅ STEP 9: CRITICAL - Fetch services with the new day IDs
    console.log('[EditDateAndCapacity] 📤 Fetching services with fresh day IDs...');
    setServicesLoading(true);
    try {
      const currentLang = i18n?.language || 'en';
      const bookingIdToUse = freshBookingData.id || bookingId;
      const services = await fetchServiceOptions(bookingIdToUse, currentLang);
      
      setServicesData(services || {});
      
      if (services && Object.keys(services).length > 0) {
        const firstDayId = Object.keys(services)[0];
        setSelectedServiceDayId(firstDayId);
      }
      
    } catch (err) {
      console.error('[EditDateAndCapacity] Failed to fetch services:', err);
    } finally {
      setServicesLoading(false);
    }
    
    await fetchBasketData();
    
    setIsEditModalOpen(false);
    
    setTimeout(() => {
      openModal({
        title: t("booking.fullBookingPage.editBooking.saveSuccessHeader", "Success"),
        content: <p>{t("booking.fullBookingPage.editBooking.saveSuccess", "Booking details updated successfully")}</p>,
        type: "success",
        autoClose: true,
        closeDelay: 2000,
      });
    }, 100);
    
  } catch (err) {
    console.error('[EditDateAndCapacity] ❌ Save failed:', err);

    if (isMountedRef.current && !step1Persisted) {
      updateFormData(originalFormData);
      setBookingDays(originalBookingDays);
      setCurrentDayId(originalCurrentDayId);
      setSelectedServiceDayId(originalSelectedServiceDayId);
      setBookingId(originalBookingId);
      setBasketId(originalBasketId);
      setServicesData(originalServicesData);
      dayServicesRef.current = originalFormData.day_services || [];
    }

    openModal({
      header: t("booking.fullBookingPage.editBooking.saveErrorHeader", "Error"),
      content: <p>{err.message || t("booking.fullBookingPage.editBooking.saveError", "Failed to update booking details")}</p>,
      type: "error",
      autoClose: true,
      closeDelay: 3000,
    });
  } finally {
    setIsSavingEdit(false);
    isEditingRef.current = false;
    console.log('[EditDateAndCapacity] 🏁 EDIT SAVE COMPLETE');
  }
}, [
  updateFormData, 
  updateBookingStep1, 
  waitForAllUpdates, 
  fetchBasketData, 
  openModal, 
  t, 
  bookingId, 
  basketId,
  token, 
  formData.capacity, 
  formData.coworking_stations, 
  formData.booking_hours,
  formData.disposition_type,
  formData.comment,
  formData.training_id,
  formData.externalID,
  formData.day_services,
  bookingDays,
  currentDayId,
  selectedServiceDayId,
  space,
  getBookingById,
  fetchServiceOptions,
  i18n?.language,
  setBookingDays,
  setCurrentDayId,
  setSelectedServiceDayId,
  setServicesLoading,
  setServicesData,
  setIsEditModalOpen,
  externalID,
  servicesData,
  getFilteredDispositionTypes
]);
  
  // ========== PROMO FUNCTIONS ==========
  
  const handleApplyPromo = useCallback(async () => {
    if (!promoCode || !basketId) {
      setError(t("booking.fullBookingPage.errors.promoRequired"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await applyPromo(token, basketId, promoCode, providerId);
      if (response.success) {
        setPromoAppliedDiscount(response.discount || 0);
        await fetchBasketData();
      } else {
        setError(response.errors?.[0]?.message || response.message || t("booking.fullBookingPage.errors.promoInvalid"));
        setPromoAppliedDiscount(0);
      }
    } catch (err) {
      setError(err.message || t("booking.fullBookingPage.errors.promoError"));
      setPromoAppliedDiscount(0);
    } finally {
      setSubmitting(false);
    }
  }, [promoCode, basketId, token, providerId, t, fetchBasketData]);
  
  const handlePromoApplied = useCallback(async (discountAmount) => {
    setPromoAppliedDiscount(discountAmount);
    setError(null);
    await fetchBasketData();
  }, [fetchBasketData]);
  
  // ========== SERVICE FUNCTIONS ==========
  
  const handleServiceSelect = useCallback((serviceId, quantity, dayId) => {
    let targetDayId = dayId || selectedServiceDayId || currentDayId;

    if (!targetDayId && bookingId && token) {
      void fetchCurrentDayId().then(fetchedDayId => {
        if (fetchedDayId) {
          handleServiceSelect(serviceId, quantity, fetchedDayId);
        }
      }).catch(err => {
        console.error('[handleServiceSelect] Error fetching day_id:', err);
      });
      return;
    }

    if (!targetDayId) {
      console.error('[handleServiceSelect] No day_id available, aborting');
      return;
    }

    // ✅ Check if the day exists in bookingDays
    const dayExists = bookingDays.some(day => day.id === targetDayId);
    if (!dayExists) {
      console.warn(`[handleServiceSelect] Day ${targetDayId} not found in bookingDays`);
      return;
    }

    // ✅ Check if the service exists in servicesData for this day
    const dayServices = servicesData[targetDayId];
    if (!dayServices) {
      console.warn(`[handleServiceSelect] No services found for day ${targetDayId}`);
      return;
    }

    const serviceExists = dayServices.some(s => s.service?.place_service?.id === serviceId);
    if (!serviceExists) {
      console.warn(`[handleServiceSelect] Service ${serviceId} not available for day ${targetDayId}`);
      return;
    }

    const currentDayServices = dayServicesRef.current || [];
    const updatedDayServices = currentDayServices.map(day => ({
      day_id: day.day_id,
      services: (day.services || []).map(service => ({ ...service })),
    }));

    const dayIndex = updatedDayServices.findIndex(
      day => day.day_id === targetDayId
    );

    if (dayIndex > -1) {
      const services = updatedDayServices[dayIndex].services;
      const serviceIndex = services.findIndex(
        service => service.service_id === serviceId
      );

      if (quantity <= 0) {
        if (serviceIndex > -1) {
          services.splice(serviceIndex, 1);
        }
        if (services.length === 0) {
          updatedDayServices.splice(dayIndex, 1);
        }
      } else if (serviceIndex > -1) {
        services[serviceIndex] = {
          ...services[serviceIndex],
          quantity,
        };
      } else {
        services.push({
          service_id: serviceId,
          quantity,
        });
      }
    } else if (quantity > 0) {
      updatedDayServices.push({
        day_id: targetDayId,
        services: [{ service_id: serviceId, quantity }],
      });
    }

    dayServicesRef.current = updatedDayServices;

    setFormData(prev => ({
      ...prev,
      day_services: updatedDayServices,
    }));

    if (bookingId && token && updatedDayServices.length > 0) {
      queueServiceSave(updatedDayServices);
    }
  }, [selectedServiceDayId, currentDayId, bookingId, token, fetchCurrentDayId, queueServiceSave, bookingDays, servicesData]);

  const getSelectedQuantity = useCallback((serviceId, dayId) => {
    const targetDayId = dayId || selectedServiceDayId || currentDayId;
    if (!targetDayId) return 0;
    const dayService = formData.day_services?.find(day => day.day_id === targetDayId);
    return dayService?.services?.find(s => s.service_id === serviceId)?.quantity || 0;
  }, [formData.day_services, selectedServiceDayId, currentDayId]);
  
// ============================================================
// performReservation - ALWAYS run Step 2 before confirmation
// ============================================================
const performReservation = useCallback(async () => {
  if (isPerformingRef.current) {
    console.warn('[FullBookingPage] Reservation already in progress');
    return;
  }
  
  if (isAnyUpdateInProgressForReservation()) {
    setError(t("booking.fullBookingPage.errors.savingInProgress", "Please wait for all changes to be saved..."));
    return;
  }
  
  if (!basketId || !bookingId) {
    setError(t("booking.fullBookingPage.errors.missingBookingData", "Booking data not ready. Please try again."));
    return;
  }
  
  if (formData.booking_hours.length === 0) {
    setError(t("booking.fullBookingPage.errors.slotRequired"));
    return;
  }
  
  const hasCapacity = isCoworking ? formData.coworking_stations > 0 : formData.capacity > 0;
  if (!hasCapacity) {
    setError(t("booking.fullBookingPage.errors.peopleRequired"));
    return;
  }
  
  isPerformingRef.current = true;
  isSubmittingRef.current = true;
  
  setShowLoadingOverlay(true);
  setLoadingMessage(t("booking.fullBookingPage.processing", "Processing your booking..."));
  
  try {
    if (userType === "provider") {
      setError(t("booking.fullBookingPage.errors.provider"));
      isPerformingRef.current = false;
      isSubmittingRef.current = false;
      setShowLoadingOverlay(false);
      return;
    }
    
    setLoadingMessage(t("booking.fullBookingPage.validating", "Validating your information..."));
    
    let missingFields = [];
    let fieldErrors = {};
    let requiredFields = [];
    
    if (billingType === "individual") {
      requiredFields = [
        { field: billingInfo.individualFirstName, name: t("booking.fullBookingPage.billing.firstName"), key: 'individualFirstName' },
        { field: billingInfo.individualLastName, name: t("booking.fullBookingPage.billing.lastName"), key: 'individualLastName' },
        { field: billingInfo.individualAddress, name: t("booking.fullBookingPage.billing.address"), key: 'individualAddress' },
        { field: billingInfo.individualPostalCode, name: t("booking.fullBookingPage.billing.postalCode"), key: 'individualPostalCode' },
        { field: billingInfo.individualCity, name: t("booking.fullBookingPage.billing.city"), key: 'individualCity' }
      ];
    } else {
      requiredFields = [
        { field: billingInfo.companyName, name: t("booking.fullBookingPage.billing.companyName"), key: 'companyName' },
        { field: billingInfo.companySiret, name: t("booking.fullBookingPage.billing.siret"), key: 'companySiret' },
        { field: billingInfo.companyAddress, name: t("booking.fullBookingPage.billing.address"), key: 'companyAddress' },
        { field: billingInfo.companyPostalCode, name: t("booking.fullBookingPage.billing.postalCode"), key: 'companyPostalCode' },
        { field: billingInfo.companyCity, name: t("booking.fullBookingPage.billing.city"), key: 'companyCity' }
      ];
    }
    
    requiredFields.forEach(field => {
      if (!field.field || field.field.trim() === '') {
        missingFields.push(field);
        fieldErrors[field.key] = t("booking.fullBookingPage.billing.errors.fieldRequired", { fieldName: field.name });
      }
    });
    
    if (missingFields.length > 0) {
      setBillingErrors(fieldErrors);
      const errorMessage = billingType === "individual"
        ? t("booking.fullBookingPage.billing.errors.individualMissingFields")
        : t("booking.fullBookingPage.billing.errors.companyMissingFields");
      setError(errorMessage);
      setScrollToBilling(true);
      setTimeout(() => {
        setScrollToBilling(false);
      }, 500);
      
      // ✅ Keep this - only checked here
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'echec_validation_formulaire_paiement',
          booking_id: bookingId
        });
      }
      
      isPerformingRef.current = false;
      isSubmittingRef.current = false;
      setSubmitting(false);
      setShowLoadingOverlay(false);
      return;
    }
    
    setBillingErrors({});

    // ⚠️ REMOVE this CGU check - already handled in handleReserve
    // if (!cguAccepted) {
    //   setCguError(true);
    //   setError(t("booking.fullBookingPage.errors.termsRequired"));
    //   setTimeout(() => {
    //     const termsSection = document.querySelector('.termsSection');
    //     if (termsSection) {
    //       termsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    //     }
    //   }, 100);
    //   
    //   if (window.dataLayer) {
    //     window.dataLayer.push({
    //       event: 'echec_validation_formulaire_paiement',
    //       booking_id: bookingId
    //     });
    //   }
    //   
    //   isPerformingRef.current = false;
    //   isSubmittingRef.current = false;
    //   setSubmitting(false);
    //   setShowLoadingOverlay(false);
    //   return;
    // }

    setCguError(false);

    setLoadingMessage(
      t("booking.fullBookingPage.updatingBilling", "Updating billing information...")
    );

    const billingUpdateSuccess = await updateBillingInfoBeforeConfirm();
    if (!billingUpdateSuccess) {
      setError(t(
        "booking.fullBookingPage.billing.errors.updateFailed",
        "We couldn't save your billing information. Please check it and try again."
      ));
      
      // ✅ Keep this - only checked here
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'echec_validation_formulaire_paiement',
          booking_id: bookingId
        });
      }
      
      isPerformingRef.current = false;
      isSubmittingRef.current = false;
      setSubmitting(false);
      setShowLoadingOverlay(false);
      return;
    }

    setSubmitting(true);
    
    setLoadingMessage(t("booking.fullBookingPage.savingChanges", "Saving all changes..."));
    
    await waitForAllUpdates();

    if (serviceSaveFailedRef.current) {
      throw new Error('A service update could not be saved. Please review the selected services and try again.');
    }
    
    if (isAnyUpdateInProgressForReservation()) {
      console.warn('[FullBookingPage] Updates still in progress, aborting reservation');
      setError(t("booking.fullBookingPage.errors.savingInProgress", "Please wait for all changes to be saved..."));
      
      // ✅ Keep this - only checked here
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'echec_validation_formulaire_paiement',
          booking_id: bookingId
        });
      }
      
      isPerformingRef.current = false;
      isSubmittingRef.current = false;
      setSubmitting(false);
      setShowLoadingOverlay(false);
      return;
    }
    
    // ✅ Use dayServicesRef directly
    const dayServicesToUse = dayServicesRef.current || [];
    
    // ✅ Verify day IDs against current bookingDays
    const validDayIds = bookingDays.map(d => d.id);
    
    // ✅ Filter out any invalid day IDs
    const validDayServices = dayServicesToUse
      .filter(day => validDayIds.includes(day.day_id))
      .map(day => ({
        day_id: day.day_id,
        services: day.services.filter(s => s.quantity > 0)
      }))
      .filter(day => day.services.length > 0);
    
    let latestBasketData = basketData;

    // ============================================================
    // ✅ STEP 2 - ALWAYS RUN BEFORE CONFIRMATION
    // Saves ALL booking data: comment, disposition_type, day_services, training_id
    // ============================================================
    console.log('[FullBookingPage] 📦🚀 STEP 2 - ALWAYS executing before confirmation');
    console.log('[FullBookingPage] 📌 STEP 2 - Disposition type:', formData.disposition_type);
    console.log('[FullBookingPage] 📌 STEP 2 - Comment:', formData.comment);
    console.log('[FullBookingPage] 📌 STEP 2 - Training ID:', formData.training_id);
    console.log('[FullBookingPage] 📌 STEP 2 - Day services count:', validDayServices.length);
    
    setLoadingMessage(t("booking.fullBookingPage.savingBookingDetails", "Saving booking details..."));

    if (bookingId && token && formData.booking_hours.length > 0) {
      // ✅ Prepare Step 2 data with ALL booking details
      const step2Data = {
        basket_item_id: bookingId,
        place_id: space?.id,
        comment: formData.comment || "",
        disposition_type: formData.disposition_type || null,
      };
      
      // ✅ Always include day_services (even if empty)
      if (validDayServices.length > 0) {
        step2Data.day_services = validDayServices;
      } else {
        // ✅ Explicitly send empty array to clear services if needed
        step2Data.day_services = [];
      }
      
      // ✅ Include training_id if present
      if (formData.training_id) {
        step2Data.training_id = formData.training_id;
      }
      
      // ✅ Remove null disposition_type (don't send if null)
      if (step2Data.disposition_type === null) {
        delete step2Data.disposition_type;
      }
      
      console.log('[FullBookingPage] 📦 STEP 2 data (ALWAYS SUBMITTED):', JSON.stringify(step2Data, null, 2));
      
      // ✅ Execute Step 2 with retry logic
      let step2Success = false;
      let step2Retries = 0;
      const maxStep2Retries = 3;
      let lastStep2Error = null;
      
      while (!step2Success && step2Retries <= maxStep2Retries) {
        try {
          console.log(`[FullBookingPage] 🔄 STEP 2 attempt ${step2Retries + 1}/${maxStep2Retries + 1}`);
          
          const response = await postBookingStepByStep(step2Data, token, 2, bookingId);
          
          if (response?.id) {
            step2Success = true;
            console.log('[FullBookingPage] ✅ STEP 2 success on attempt', step2Retries + 1);
            console.log('[FullBookingPage] ✅ STEP 2 response:', response);
          } else {
            throw new Error('Step 2 update returned an unsuccessful response');
          }
        } catch (step2Err) {
          console.error(`[FullBookingPage] ❌ STEP 2 attempt ${step2Retries + 1} failed:`, step2Err);
          lastStep2Error = step2Err;
          step2Retries++;
          
          if (step2Retries <= maxStep2Retries) {
            // ✅ Exponential backoff
            const delay = Math.pow(2, step2Retries) * 500;
            console.log(`[FullBookingPage] ⏳ Waiting ${delay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // ✅ If Step 2 completely failed, DO NOT proceed to confirmation
      if (!step2Success) {
        console.error('[FullBookingPage] ❌ All STEP 2 attempts failed');
        setError(lastStep2Error?.message || 'Failed to save booking details before confirmation');
        
        // ✅ Keep this - only checked here
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'echec_validation_formulaire_paiement',
            booking_id: bookingId
          });
        }
        
        isPerformingRef.current = false;
        isSubmittingRef.current = false;
        setSubmitting(false);
        setShowLoadingOverlay(false);
        return;
      }
      
      // ✅ Refresh basket data after Step 2
      latestBasketData = await fetchBasketData();
      if (!latestBasketData) {
        console.warn('[FullBookingPage] ⚠️ Failed to refresh basket data after Step 2');
        // Don't throw here, continue with confirmation
      }
    } else {
      console.warn('[FullBookingPage] ⚠️ No bookingId or token, skipping Step 2');
    }
    
    // ============================================================
    // ✅ CONFIRM THE BASKET (Step 3)
    // ============================================================
    console.log('[FullBookingPage] 📦 CONFIRMING booking:', bookingId);
    setLoadingMessage(t("booking.fullBookingPage.confirming", "Confirming your booking..."));
    
    const filteredUtms = getUtmsForConfirmation();
    
    const confirmPayload = { 
      basket_id: basketId,
      booking_id: bookingId,
      ...filteredUtms
    };
    
    if (formData.training_id) {
      confirmPayload.training_id = formData.training_id;
    }
    
    console.log('[FullBookingPage] 📦 Confirm payload:', confirmPayload);
    
    const confirmResponse = await confirmBasket(token, confirmPayload);
    console.log('[FullBookingPage] ✅ Confirm response:', confirmResponse);
    
    const isHundredPercentDiscount = promoPercentage === 100;
    const isTotalZero =
      confirmResponse?.data?.total === 0 ||
      confirmResponse?.data?.discounted_total === 0 ||
      basketData?.discounted_total === 0 ||
      formData?.total_days === 0;
    const isFullyDiscounted = isHundredPercentDiscount || isTotalZero;
    const redirectUrl = confirmResponse?.data?.redirect_url;
    
    setLoadingMessage(t("booking.fullBookingPage.redirecting", "Redirecting you..."));
    
    if (isTrainee) {
      navigate("/dashboard/bookings");
      setTimeout(() => {
        openModal({
          content: <p>{t("booking.trainee.successDetails", "Your training booking request has been successfully submitted.")}</p>,
          size: "medium", 
          type: "success", 
          autoClose: false, 
          closeOnOutsideClick: false,
          title: t("booking.trainee.successTitle", "Training Booking Submitted"),
        });
        setShowLoadingOverlay(false);
      }, 100);
    } else if (isFullyDiscounted) {
      navigate("/dashboard/bookings");
      setTimeout(() => {
        openModal({
          content: <p>{t("booking.success.modalDetails", "Your booking request has been successfully submitted.")}</p>,
          size: "medium", 
          type: "success", 
          autoClose: false, 
          closeOnOutsideClick: false,
          title: t("booking.success.modalTitle", "Booking Request Submitted"),
        });
        setShowLoadingOverlay(false);
      }, 100);
    } else if (redirectUrl) {

        // ============================================================
  // ✅ TRACK REDIRECTION TO PAYLINE (ONLY ON SUCCESS)
  // This fires ONLY after all validation has passed
  // ============================================================
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'redirection_payline_reussie',
      'booking_id': bookingId,
      'basket_id': basketId,
      'timestamp': new Date().toISOString()
    });
  }
      try {
        sessionStorage.setItem('booking_success', JSON.stringify({
          bookingId: bookingId,
          basketId: basketId,
          timestamp: Date.now()
        }));
      } catch (storageError) {
        console.warn('[FullBookingPage] Could not store booking success state:', storageError);
      }

      window.location.href = redirectUrl;
    } else {
      navigate("/dashboard/bookings");
      setTimeout(() => {
        openModal({ 
          content: <p>{t("booking.fullBookingPage.bookingSuccess.message")}</p>, 
          type: "success", 
          title: t("booking.fullBookingPage.bookingSuccess.title") 
        });
        setShowLoadingOverlay(false);
      }, 100);
    }
  } catch (err) {
    console.error('Reservation error:', err);
    setShowLoadingOverlay(false);
    
    // ✅ Keep this - only checked here
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'echec_validation_formulaire_paiement',
        booking_id: bookingId
      });
    }
    
    if (err.response?.data?.code === "PROMO_CODE_ERROR" || err.message?.includes("promo") || err.message?.includes("discount")) {
      setTimeout(() => {
        openModal({
          content: (
            <div className={styles.errorModal}>
              <h2>{t("booking.error.promoTitle", "Promo Code Issue")}</h2>
              <p>{err.message || t("booking.error.promoMessage", "An error occurred while processing your booking. Please try again later.")}</p>
            </div>
          ),
          size: "small", 
          type: "error", 
          autoClose: false, 
          closeOnOutsideClick: false,
        });
      }, 100);
    } else if (err.message?.includes("day_id") || err.message?.includes("day_services")) {
      console.error('[FullBookingPage] Day ID error detected, refreshing data...');
      try {
        const refreshData = await getBookingById(bookingId, token, 2);
        if (refreshData?.booking_days) {
          setBookingDays(refreshData.booking_days);
          if (refreshData.booking_days[0]?.id) {
            setCurrentDayId(refreshData.booking_days[0].id);
            setSelectedServiceDayId(refreshData.booking_days[0].id);
          }
          updateFormData({ 
            day_services: refreshData.day_services || [],
            booking_hours: refreshData.booking_hours || formData.booking_hours,
            capacity: refreshData.capacity ?? formData.capacity,
            coworking_stations: refreshData.coworking_stations ?? formData.coworking_stations
          });
          dayServicesRef.current = refreshData.day_services || [];
        }
      } catch (refreshErr) {
        console.error('[FullBookingPage] Failed to refresh data after day ID error:', refreshErr);
      }
      setError(t("booking.fullBookingPage.errors.dayIdError", "There was an issue with your booking dates. Please try again."));
    } else {
      setError(err.message || t("booking.fullBookingPage.errors.reservationFailed"));
    }
  } finally {
    setSubmitting(false);
    isPerformingRef.current = false;
    isSubmittingRef.current = false;
    setTimeout(() => {
      setShowLoadingOverlay(false);
    }, 1000);
  }
}, [
  userType, 
  billingType, 
  billingInfo, 
  t, 
  updateBillingInfoBeforeConfirm, 
  cguAccepted, 
  token, 
  basketId, 
  bookingId, 
  formData, 
  getUtmsForConfirmation, 
  promoPercentage, 
  basketData, 
  isTrainee, 
  navigate, 
  openModal, 
  space, 
  isCoworking,
  isAnyUpdateInProgressForReservation,
  waitForAllUpdates,
  fetchBasketData,
  updateFormData,
  currentDayId,
  setCurrentDayId,
  setSelectedServiceDayId,
  setBookingDays
]);
  
// ============================================================
// handleReserve
// ============================================================
const handleReserve = useCallback(async () => {
  setError(null);
  setSlotError(null);
  setFormErrors({});
  setBillingErrors({});
  setCguError(false);
  
  if (isAnyUpdateInProgressForReservation()) {
    setError(t("booking.fullBookingPage.errors.savingInProgress", "Please wait for all changes to be saved..."));
    return;
  }
  
  if (formData.booking_hours.length === 0) {
    setSlotError(t("booking.fullBookingPage.errors.slotRequired"));
    return;
  }
  
  const hasCapacity = isCoworking ? formData.coworking_stations > 0 : formData.capacity > 0;
  if (!hasCapacity) {
    setError(t("booking.fullBookingPage.errors.peopleRequired"));
    return;
  }
  
  if (dispositionTypesList.length > 0 && !formData.disposition_type) {
    setFormErrors({ disposition_type: t("booking.fullBookingPage.errors.layoutRequired") });
    
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'echec_validation_formulaire_paiement',
        booking_id: bookingId
      });
    }
    
    setTimeout(() => {
      const dispositionSection = document.getElementById('disposition-section');
      if (dispositionSection) {
        const headerOffset = 80;
        const elementPosition = dispositionSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
      if (dispositionRef.current) {
        dispositionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
      }
    }, 100);
    return;
  }
  
  if (!cguAccepted) {
    setCguError(true);
    
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'echec_validation_formulaire_paiement',
        booking_id: bookingId
      });
    }
    
    setTimeout(() => {
      const termsSection = document.querySelector('.termsSection');
      if (termsSection) termsSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return;
  }
  
  setCguError(false);
  
  if (!isAuthenticated) {
    const stored = storeBookingDataForLater();

    if (!stored) {
      setError(
        t(
          "booking.fullBookingPage.errors.storageFailed",
          "We couldn't temporarily save your booking. Please try again."
        )
      );
      return;
    }

    openModal({
      content: (
        <Login
          forBookingForm={true}
          spaceId={spaceId}
          onSuccess={() => {
            closeModal();

            try {
              const storedData = sessionStorage.getItem(
                'pending_full_booking_data'
              );

              if (!storedData) {
                setError(
                  t(
                    "booking.fullBookingPage.errors.missingBookingData",
                    "Booking data not ready. Please try again."
                  )
                );
                return;
              }

              const parsedData = JSON.parse(storedData);
              sessionStorage.removeItem('pending_full_booking_data');

              if (!parsedData?.formData || !parsedData?.billingInfo) {
                throw new Error('Stored booking data is incomplete');
              }

              setFormData(parsedData.formData);
              setBillingInfo(parsedData.billingInfo);
              setBillingType(parsedData.billingType || 'individual');
              setPromoCode(parsedData.promoCode || '');

              if (parsedData.selectedDate) {
                setSelectedDate(createStableLocalDate(parsedData.selectedDate));
              }
              setSelectedDuration(parsedData.selectedDuration ?? null);
              setStartTime(parsedData.startTime ?? null);
              setEndTime(parsedData.endTime ?? null);

              setResumeReservationAfterLogin(true);
            } catch (restoreError) {
              console.error(
                '[FullBookingPage] Failed to restore pending booking:',
                restoreError
              );
              setError(
                t(
                  "booking.fullBookingPage.errors.missingBookingData",
                  "Booking data not ready. Please try again."
                )
              );
            }
          }}
        />
      ),
      title: t("modal.login.title"),
    });
    return;
  }

  await performReservation();
}, [formData, isCoworking, space, t, cguAccepted, isAuthenticated, storeBookingDataForLater, openModal, closeModal, spaceId, performReservation, isAnyUpdateInProgressForReservation, dispositionTypesList, bookingId]);
  
  // ========== EFFECTS ==========
  
  useEffect(() => {
    if (!resumeReservationAfterLogin) return;

    setResumeReservationAfterLogin(false);
    void performReservation();
  }, [resumeReservationAfterLogin, performReservation]);

  // ============================================================
  // REMOVED: Automatic useEffect that was calling updateBookingStep1()
  // This was causing a second Step 1 call after edit.
  // updateBookingStep1() is now only called from:
  // - handleEditSave (when user saves from modal)
  // - handleAddTimeSlot (when user adds a time slot)
  // - handleRemoveTimeSlot (when user removes a time slot)
  // ============================================================

  useEffect(() => {
    if (bookingId && token && !currentDayId) {
      fetchCurrentDayId().then(dayId => {
        if (dayId) {
          console.log('[FullBookingPage] Current day_id initialized:', dayId);
        }
      });
    }
  }, [bookingId, token, currentDayId, fetchCurrentDayId]);
  
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      isPerformingRef.current = false;
      isUpdatingStep1Ref.current = false;
      isUpdatingServicesRef.current = false;
      
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      
      if (isServicesSavingRef.current) {
        isServicesSavingRef.current = false;
        setIsServicesSaving(false);
      }
    };
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };
    if (isCalendarOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);
  
  // ============================================================
  // Load availability when date is selected
  // ============================================================
  useEffect(() => {
    if (!selectedDate) return;

    const requestId = ++availabilityRequestIdRef.current;

    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    fetchTimeoutRef.current = setTimeout(async () => {
      setLoadingAvailability(true);

      try {
        const booked = await fetchBookingsForDate(selectedDate);

        if (
          !isMountedRef.current ||
          requestId !== availabilityRequestIdRef.current
        ) {
          return;
        }

        if (booked === null) {
          return;
        }

        setBookedSlots(booked);

        const availableDurs = [];

        for (const opt of durationOptions) {
          const slots = getAvailableTimeSlots(selectedDate, opt.value, booked);
          if (slots.length > 0) {
            availableDurs.push(opt.value);
          }
        }

        setAvailableDurations(availableDurs);
        setSelectedDuration(null);
        setStartTime(null);
        setEndTime(null);
        setAvailableSlots([]);
      } catch (err) {
        if (isMountedRef.current && requestId === availabilityRequestIdRef.current) {
          console.error('[FullBookingPage] Failed to calculate availability:', err);
          setBookedSlots([]);
          setAvailableDurations([]);
          setAvailableSlots([]);
        }
      } finally {
        if (isMountedRef.current && requestId === availabilityRequestIdRef.current) {
          setLoadingAvailability(false);
        }
      }
    }, 250);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [selectedDate, durationOptions, getAvailableTimeSlots, fetchBookingsForDate]);

  // ============================================================
  // Calculate end time when start time is selected
  // ============================================================
  useEffect(() => {
    if (startTime && selectedDuration) {
      const endHour = timeToHour(startTime) + selectedDuration;
      setEndTime(formatTimeDisplay(hourToTime(endHour)));
    } else {
      setEndTime(null);
    }
  }, [startTime, selectedDuration]);
  
  // ========== LOAD DATA ==========
  
  useEffect(() => {
    const requestId = ++dataLoadRequestIdRef.current;

    const loadData = async () => {
      try {
        setLoading(true);
        
        const bookingPromise =
          bookingIdFromUrl && token
            ? getBookingById(bookingIdFromUrl, token, 2)
            : Promise.resolve(null);

        const [spaceData, initialBookingData] = await Promise.all([
          fetchSpaceData(spaceId, i18n?.language),
          bookingPromise,
        ]);

        if (!isMountedRef.current || requestId !== dataLoadRequestIdRef.current) {
          return;
        }
        
        if (spaceData) {
          const hasBinaryData = [0, 1, 2, 3, 4, 5, 6].some(i => spaceData[`day_${i}`]);
          if (hasBinaryData && spaceData.opening_hours) {
            const correctedOpeningHours = [];
            for (let i = 0; i < 7; i++) {
              const dayKey = `day_${i}`;
              const binaryStr = spaceData[dayKey];
              if (binaryStr) {
                const slots = parseBinaryToTimeSlots(binaryStr);
                slots.forEach(slot => {
                  correctedOpeningHours.push({
                    day: i,
                    from: slot.from + ':00',
                    to: slot.to + ':00'
                  });
                });
              }
            }
            if (correctedOpeningHours.length > 0) {
              spaceData.opening_hours = correctedOpeningHours;
            }
          }
        }
        
        setSpace(spaceData);
        
        if (bookingIdFromUrl) {
          if (!token) {
            return;
          }

          const bookingData = initialBookingData;

          if (!bookingData) {
              setFatalError('Booking not found');
              setLoading(false);
              return;
            }
            if (bookingData.trainings && bookingData.trainings.length > 0) setTrainings(bookingData.trainings);
            if (bookingData.provider_id) setProviderId(bookingData.provider_id);
            if (bookingData.promo_code_id) setPromoCode(bookingData.promo_code_id);
            
            if (bookingData.booking_days) {
              setBookingDays(bookingData.booking_days);
            }
            
            const bookingHours = bookingData.booking_hours || [];
            
            let parsedSelectedDate = null;
            let parsedStartTime = null;
            let parsedEndTime = null;
            let parsedSelectedDuration = null;
            
            if (bookingHours.length > 0) {
              const slot = bookingHours[0];
              if (slot.date) {
                parsedSelectedDate = createStableLocalDate(slot.date);
              }
              if (slot.from) {
                parsedStartTime = slot.from;
              }
              if (slot.to) {
                parsedEndTime = slot.to;
              }
              if (slot.from && slot.to) {
                parsedSelectedDuration = calculateDuration(slot.from, slot.to);
              }
            }
            
            setFormData({
              capacity: bookingData.capacity ?? 1,
              coworking_stations: bookingData.coworking_stations ?? 0,
              booking_hours: bookingHours,
              day_services: bookingData.day_services || [],
              comment: bookingData.comment || "",
              disposition_type: bookingData.disposition_type || null,
              training_id: bookingData.training_id || null,
              externalID: bookingData.externalID || externalID || null,
            });
            
            setBasketId(bookingData.basket_id);
            setBookingId(bookingData.id);
            
            if (bookingData.booking_days?.[0]?.id) {
              setCurrentDayId(bookingData.booking_days[0].id);
              setSelectedServiceDayId(bookingData.booking_days[0].id);
            }
            
            if (parsedSelectedDate) {
              setSelectedDate(parsedSelectedDate);
            }
            if (parsedStartTime) {
              setStartTime(parsedStartTime);
            }
            if (parsedEndTime) {
              setEndTime(parsedEndTime);
            }
            if (parsedSelectedDuration) {
              setSelectedDuration(parsedSelectedDuration);
            }
        }
        
      } catch (err) {
        if (isMountedRef.current && requestId === dataLoadRequestIdRef.current) {
          setFatalError(
            err.response?.status === 404 ? 'Booking not found' : err.message
          );
        }
      } finally {
        if (isMountedRef.current && requestId === dataLoadRequestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void loadData();

    return () => {
      dataLoadRequestIdRef.current++;
    };
  }, [spaceId, token, bookingIdFromUrl, i18n?.language, externalID]);
  
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const isCompanyUser = user.status === "company";

    setBillingType(isCompanyUser ? "company" : "individual");
    setBillingInfo({
      individualFirstName: user.first_name || "",
      individualLastName: user.last_name || "",
      individualAddress: user.address?.address || "",
      individualPostalCode: user.address?.postal_code || "",
      individualCity: user.address?.city || "",
      companyName: user.social_reason || "",
      companyVatNumber: user.vat_number || "",
      companySiret: user.company_number || "",
      companyAddress: user.company_address?.address || "",
      companyPostalCode: user.company_address?.postal_code || "",
      companyCity: user.company_address?.city || "",
      vat_taxable_person:
        user.vat_taxable_person !== undefined
          ? user.vat_taxable_person
          : (user.vat_number ? 1 : 0),
      user_status: isCompanyUser ? "company" : "individual",
    });
  }, [isAuthenticated, user]);

  useEffect(() => {
    const fetchProviderFromBooking = async () => {
      if (basketId && bookingId && !providerId && token) {
        try {
          const bookingData = await getBookingById(bookingId, token, 1);
          if (bookingData && bookingData.provider_id) setProviderId(bookingData.provider_id);
        } catch (err) {
          console.error('Failed to fetch provider_id:', err);
        }
      }
    };
    fetchProviderFromBooking();
  }, [basketId, bookingId, token, providerId]);
  
  useEffect(() => {
    if (basketId && token) fetchBasketData();
  }, [basketId, token, fetchBasketData]);
  
  useEffect(() => {
    if (!space || !bookingId || !token) return;

    const requestId = ++servicesLoadRequestIdRef.current;
    let active = true;

    const loadServices = async () => {
      setServicesLoading(true);

      try {
        const currentLang = i18n?.language || 'en';
        const services = await fetchServiceOptions(bookingId, currentLang);

        if (
          !active ||
          !isMountedRef.current ||
          requestId !== servicesLoadRequestIdRef.current
        ) {
          return;
        }

        setServicesData(services || {});

        if (!currentDayIdRef.current && services && Object.keys(services).length > 0) {
          const firstServiceDayId = Object.keys(services)[0];
          setCurrentDayId(firstServiceDayId);
          setSelectedServiceDayId(firstServiceDayId);
        }
      } catch (err) {
        if (active && isMountedRef.current) {
          console.error('[FullBookingPage] Failed to load services:', err);
        }
      } finally {
        if (active && isMountedRef.current) {
          setServicesLoading(false);
        }
      }
    };

    void loadServices();

    return () => {
      active = false;
      servicesLoadRequestIdRef.current++;
    };
  }, [space, bookingId, token, i18n?.language]);

  // ========== RENDER HELPERS ==========
  
  const getDaysInMonth = useCallback((date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  }, []);
  
  const getDateStatus = useCallback((date) => {
    if (!date) return 'empty';
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return 'empty';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minAllowedDate = getMinAllowedDate();
    if (stableDate < minAllowedDate) return 'disabled';
    if (stableDate < today) return 'past';
    const isWorking = isWorkingDay(date);
    if (!isWorking) return 'unavailable';
    const isToday = stableDate.getTime() === today.getTime();
    if (isToday) return 'disabled';
    return 'available';
  }, [isWorkingDay, getMinAllowedDate]);
  
  // ========== RENDER ==========
  
  if (showLoadingOverlay) {
    return <LoadingOverlay 
      message={loadingMessage} 
      subMessage={t("booking.fullBookingPage.pleaseWait", "Please wait, this may take a moment")}
    />;
  }
  
  if (loading) {
    return (
      <div className={styles.fullBookingPage}>
        <div className={styles.container}>
          <div className={styles.loadingState}>
            <div className={styles.spinnerSmall}></div>
            <p>{t("booking.fullBookingPage.loading")}</p>
          </div>
        </div>
      </div>
    );
  }
  
if (!isAuthenticated && !loading) {
  return (
    <div className="pageContainer">
      <div className={styles.container}>
        <div className={styles.loginRequiredSection}>
          <div className={styles.loginRequiredContent}>
            <h1 className={styles.loginRequiredTitle}>
              {t("booking.fullBookingPage.loginRequiredTitle")}
            </h1>
            <p className={styles.loginRequiredText}>
              {t("booking.fullBookingPage.loginRequiredText")}
            </p>
            <button
              type="button"
              className={styles.loginRequiredButton}
              onClick={handleReopenLogin}
            >
              {t("modal.login.login")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
  
  if (fatalError || !space) return <NotFoundPage />;
  
  const showDisposition = !isCoworking && dispositionTypesList.length > 0;
  const showTrainingSelector = space?.trainee_bookable && isTrainee && trainings.length > 0;
  
  const isServicesOperationInProgress = isServicesSaving || 
                                       isServicesSavingRef.current || 
                                       serviceSaveQueue.current.length > 0 ||
                                       isServiceSaving.current;
  
  const isSubmitDisabled = isAnyUpdateInProgress || 
                          submitting || 
                          formData.booking_hours.length === 0 ||
                          isServicesOperationInProgress;
  
  return (
    <div className={styles.fullBookingPage}>
      <div className={styles.container}>
        <div className={styles.bookingHeader}>
          <button onClick={() => navigate(addUTMsToUrl(`/space/${spaceId}`))} className={styles.backButton}>
            ← {t("booking.fullBookingPage.previous")}
          </button>
          <div className={styles.headerCenter}>
            <h1 className={styles.bookingTitle}>
              {t("booking.fullBookingPage.title")}
              <span className={styles.bookingStep}>{t("booking.fullBookingPage.step")}</span>
            </h1>
          </div>
          <button className={styles.helpButton} onClick={() => window.open("/contact", "_blank")}>
            {t("booking.fullBookingPage.help")}
          </button>
        </div>
        
        <div className={styles.twoColumnLayout}>
          <div className={styles.formColumn}>
            <BookingDetails 
              viewOnly={true}
              isCoworking={isCoworking}
              formData={formData}
              updateFormData={updateFormData}
              maxCapacity={maxCapacity}
              selectedDate={selectedDate}
              startTime={startTime}
              endTime={endTime}
              selectedDuration={selectedDuration}
              availableSlots={availableSlots}
              durationOptions={durationOptions}
              availableDurations={availableDurations}
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              isCalendarOpen={isCalendarOpen}
              setIsCalendarOpen={setIsCalendarOpen}
              calendarRef={calendarRef}
              setSelectedDate={handleDateSelect}
              setSelectedDuration={setSelectedDuration}
              setStartTime={setStartTime}
              handleAddTimeSlot={handleAddTimeSlot}
              handleRemoveTimeSlot={handleRemoveTimeSlot}
              slotError={slotError}
              getDaysInMonth={getDaysInMonth}
              getDateStatus={getDateStatus}
              formatDateToString={formatDateToString}
              loadingAvailability={loadingAvailability}
              t={t}
              i18n={i18n}
              spaceOpeningHours={space?.opening_hours}
              isUpdating={submitting || isSlotOperationInProgress}
              onEditClick={() => setIsEditModalOpen(true)}
              isSaving={isSavingEdit}
            />
            
            {showDisposition && (
              <DispositionTypes 
                ref={dispositionRef}
                space={space} 
                formData={formData} 
                setFormData={updateFormData} 
                errors={formErrors} 
                capacity={formData.capacity}
                t={t}
              />
            )}
            
            <ServicesSection 
              servicesData={servicesData}
              selectedServiceDayId={selectedServiceDayId}
              getSelectedQuantity={getSelectedQuantity}
              handleServiceSelect={handleServiceSelect}
              spaceCurrency={space?.currency}
              bookingHours={formData.booking_hours}
              bookingDays={bookingDays}
              onDayChange={setSelectedServiceDayId}
              t={t}
              isLoading={servicesLoading || !currentDayId}
              capacity={isCoworking ? formData.coworking_stations : formData.capacity}
            />
            
            {showTrainingSelector ? (
              <TrainingIdSelector
                trainings={trainings}
                trainingId={formData.training_id || ""}
                onSelect={(value) => updateFormData({ training_id: value })}
                t={t}
              />
            ):null}
            
            <AdditionalComments 
              comment={formData.comment}
              updateFormData={updateFormData}
              t={t}
            />
            
            {!isTrainee && (
              <BillingInformation 
                billingType={billingType}
                setBillingType={setBillingType}
                billingInfo={billingInfo}
                setBillingInfo={setBillingInfo}
                t={t}
                errors={billingErrors}
                isUpdating={isUpdatingBillingInfo}
                onUpdate={updateBillingInfoOnServer}
                userStatus={user?.status || "individual"}
                scrollToBilling={scrollToBilling}
              />
            )}
          </div>
          
          <BookingOverview 
            space={space}
            formData={formData}
            servicesData={servicesData}
            basketData={basketData}
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            isCoworking={isCoworking}
            dispositionTypes={dispositionTypesList}
            submitting={submitting}
            handleReserve={handleReserve}
            t={t}
            basketId={basketId}
            token={token}
            providerId={providerId}
            onPromoApplied={handlePromoApplied}
            hasDiscount={hasDiscount}
            displayDiscount={displayDiscount}
            displayTotal={displayTotal}
            displayOriginalTotal={displayOriginalTotal}
            promoPercentage={promoPercentage}
            isTrainee={isTrainee}
            cguAccepted={cguAccepted}
            setCguAccepted={setCguAccepted}
            cguError={cguError}
            setCguError={setCguError}
            isSubmitDisabled={isSubmitDisabled}
            isServicesSaving={isServicesOperationInProgress || isAnyUpdateInProgress}
            basketRefreshKey={basketRefreshKey}
            queueLength={serviceSaveQueue.current.length}
            isProcessingQueue={isServiceSaving.current}
          />
        </div>
        
        {error && !cguError && <div className={styles.globalError}>{error}</div>}
      </div>
      
      {/* Edit Date & Capacity Modal */}
      <EditDateAndCapacityModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        space={space}
        formData={formData}
        isCoworking={isCoworking}
        maxCapacity={maxCapacity}
        onSave={handleEditSave}
        isSaving={isSavingEdit}
        t={t}
        currentLanguage={i18n?.language || 'en'}
      />
    </div>
  );
};

export default FullBookingPage;